/**
 * OraculoVoice — conversacion de VOZ en tiempo real con el Oraculo (Grok Voice / xAI).
 *
 * Flujo: pide token efimero al backend → WebSocket directo navegador↔xAI →
 * audio bidireccional PCM16 24kHz → las tools se ejecutan contra investments-api
 * con el JWT del usuario → si el Oraculo prepara una operacion (tradeToken),
 * aparece la tarjeta de confirmacion y el MISMO flujo SMS del chat de texto.
 *
 * La voz NUNCA ejecuta dinero: prepara. Pantalla + SMS confirman.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { oraculoSendCode, oraculoVerifyTrade, oraculoExecuteTrade, oraculoExecuteReallocation } from '../lib/cerebro';
import API from '../lib/api';

const NAVY = '#0A2540';
const GOLD = '#C9A961';

const VOICE_API = axios.create({
  baseURL: import.meta.env.VITE_INVESTMENTS_API_URL || 'http://localhost:5001',
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
});
VOICE_API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---------- utilidades de audio PCM16 <-> base64 ----------
function floatTo16BitPCM(float32) {
  const out = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}
function int16ToBase64(int16) {
  const bytes = new Uint8Array(int16.buffer);
  let bin = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}
function base64ToFloat32(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const int16 = new Int16Array(bytes.buffer);
  const float32 = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 0x8000;
  return float32;
}

export default function OraculoVoice({ phone, onClose, basePath = '/api/oraculo', onIrAlChat }) {
  const [status, setStatus] = useState('conectando'); // conectando | escuchando | hablando | error | cerrado
  const [errorMsg, setErrorMsg] = useState(null);
  const [caption, setCaption] = useState('');
  const [tradePreview, setTradePreview] = useState(null); // { tradeToken, preview, totals }
  const [opciones, setOpciones] = useState(null);         // get_opciones: la tabla va EN PANTALLA
  const [smsStep, setSmsStep] = useState(null); // null | 'sending' | 'code' | 'executing' | 'done'
  const [esDemo, setEsDemo] = useState(false);   // cuenta en allowlist: sin SMS real
  const [smsCode, setSmsCode] = useState('');
  const [smsError, setSmsError] = useState(null);
  const [capsula, setCapsula] = useState(null); // { title, audioUrl }
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [numForm, setNumForm] = useState(null); // { partialToken, datos } — mini-form voz: cedula + cuenta
  const [alChat, setAlChat] = useState(null); // { mensaje } — la voz no toma estos datos: puerta al chat
  const [numDoc, setNumDoc] = useState('');
  const [numCta, setNumCta] = useState('');
  const [numBusy, setNumBusy] = useState(false);
  const [numErr, setNumErr] = useState(null);
  // F-E por voz: formulario de cuenta de EE.UU. (cuenta + routing + direccion tecleados)
  const [intlForm, setIntlForm] = useState(null); // { partialToken, datos:{ownerName, bankName} }
  const [intlF, setIntlF] = useState({ accountNumber: '', routingNumber: '', accountType: 'checking', street: '', city: '', state: '', postalCode: '' });
  const [intlBusy, setIntlBusy] = useState(false);
  const [intlErr, setIntlErr] = useState(null);
  // Metas por voz: reequilibrio de UN TOQUE (sin SMS — no sale dinero de la cuenta)
  const [reallocTok, setReallocTok] = useState(null);
  const [reallocBusy, setReallocBusy] = useState(false);
  const [reallocErr, setReallocErr] = useState(null);
  // Blockchain: tarjeta de deposito (copiar) y formulario para PEGAR la direccion destino
  const [cryptoDeposit, setCryptoDeposit] = useState(null); // { red, activo, direccion, advertencia }
  const [copiado, setCopiado] = useState(false);
  const [cryptoForm, setCryptoForm] = useState(null); // { partialToken, montoUsd }
  const [rechargeCard, setRechargeCard] = useState(null); // { montoCop, url } -> boton PSE listo
  // Conversion COP<->USD cotizada por voz (Business): tarjeta con boton que llama el
  // endpoint protegido /transaction/convert — la voz cotiza, el dueno confirma tocando.
  const [convertCard, setConvertCard] = useState(null); // { direccion, monto, debita, comision, recibeAprox, tasa }
  const [convertBusy, setConvertBusy] = useState(false);
  const [convertMsg, setConvertMsg] = useState(null); // { ok, text }
  const [cobroCard, setCobroCard] = useState(null); // { link, cliente, concepto, montoCop, mensaje }
  const [cobroCopiado, setCobroCopiado] = useState(false);
  const [cryptoAddr, setCryptoAddr] = useState('');
  const [cryptoBusy, setCryptoBusy] = useState(false);
  const [cryptoErr, setCryptoErr] = useState(null);

  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const micStreamRef = useRef(null);
  const micNodeRef = useRef(null);
  const playTimeRef = useRef(0);
  const scheduledRef = useRef([]);
  const transcriptRef = useRef([]);
  const closedRef = useRef(false);
  const timerRef = useRef(null);
  const sessionStartRef = useRef(null); // inicio real de la sesion (para el refund de minutos)

  // ---------- reproduccion del audio del Oraculo ----------
  const playDelta = useCallback((b64) => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    const float32 = base64ToFloat32(b64);
    if (!float32.length) return;
    const buf = ctx.createBuffer(1, float32.length, 24000);
    buf.getChannelData(0).set(float32);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    const startAt = Math.max(ctx.currentTime + 0.02, playTimeRef.current);
    src.start(startAt);
    playTimeRef.current = startAt + buf.duration;
    scheduledRef.current.push(src);
    src.onended = () => {
      scheduledRef.current = scheduledRef.current.filter((s) => s !== src);
      if (scheduledRef.current.length === 0 && !closedRef.current) setStatus('escuchando');
    };
    setStatus('hablando');
  }, []);

  const flushPlayback = useCallback(() => {
    scheduledRef.current.forEach((s) => { try { s.stop(); } catch { /* ya paro */ } });
    scheduledRef.current = [];
    if (audioCtxRef.current) playTimeRef.current = audioCtxRef.current.currentTime;
  }, []);

  // ---------- transcripciones (alimentan a Clara / ontologia) ----------
  const pushTranscript = useCallback((role, text) => {
    const t = String(text || '').trim();
    if (!t) return;
    transcriptRef.current.push({ role, text: t });
    if (role === 'assistant') setCaption(t.slice(0, 180));
  }, []);

  const flushLog = useCallback(async () => {
    const events = transcriptRef.current.splice(0, 50);
    if (!events.length) return;
    try { await VOICE_API.post(`${basePath}/voice-log`, { events }); } catch { /* best effort */ }
  }, []);

  // ---------- cierre ordenado ----------
  const closeSession = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    clearInterval(timerRef.current);
    try { wsRef.current?.close(); } catch { /* ya cerrado */ }
    try { micNodeRef.current?.disconnect(); } catch { /* ya desconectado */ }
    try { micStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* sin mic */ }
    try { audioCtxRef.current?.close(); } catch { /* ya cerrado */ }
    flushLog();
    // Devolver al presupuesto mensual los minutos NO usados (la reserva se
    // cobra completa al abrir; sin esto una llamada corta quemaba 10 min).
    if (sessionStartRef.current) {
      const usedSeconds = Math.ceil((Date.now() - sessionStartRef.current) / 1000);
      sessionStartRef.current = null;
      VOICE_API.post(`${basePath}/voice-session-end`, { usedSeconds }).catch(() => {});
    }
    setStatus('cerrado');
  }, [flushLog]);

  const handleExit = useCallback(() => { closeSession(); onClose?.(); }, [closeSession, onClose]);

  // ---------- tools: el navegador ejecuta contra investments-api ----------
  const runTool = useCallback(async (name, callId, argsJson) => {
    let input = {};
    try { input = argsJson ? JSON.parse(argsJson) : {}; } catch { input = {}; }
    let result;
    try {
      const resp = await VOICE_API.post(`${basePath}/voice-tool`, { name, input }, { timeout: 30000 });
      result = resp?.data?.data ?? { error: 'Sin respuesta' };
    } catch (err) {
      result = { error: err?.response?.data?.message || 'La herramienta fallo.' };
    }

    // OPCIONES: dictar veinte contratos con su strike y su vencimiento es
    // imposible de seguir de oido. La voz dice el resumen y la tabla se pinta
    // aqui, igual que la direccion de blockchain: lo que son datos, se ven.
    if (result?.contratos?.length) {
      setOpciones({
        symbol: result.symbol,
        tipo: result.tipo,
        contratos: result.contratos,
        vencimientos: result.vencimientos || [],
        vencimientoMostrado: result.vencimiento_mostrado,
        notaPrecio: result.nota_precio,
        advertencia: result.advertencia,
      });
    }

    // Operacion preparada → tarjeta de confirmacion + flujo SMS (igual que el chat)
    if (result?.tradeToken) {
      setTradePreview({
        tradeToken: result.tradeToken,
        preview: result.preview || [],
        totalNotional: result.total_notional,
        totalFees: result.total_fees,
        totalDebit: result.total_debit,
      });
      setSmsStep(null); setSmsCode(''); setSmsError(null);
    }
    // NOMINA: la voz rechazo datos dictados (cuenta, cedula, direccion).
    // El backend hace bien en no aceptarlos, pero decir "escribemelo en el chat"
    // mientras este modal TAPA el chat es una salida sin puerta — Ivan lo vio en
    // la primera prueba. Aqui se abre la puerta: una tarjeta con el motivo y un
    // boton que cierra la voz y lleva al chat.
    if (result?.rechazado && result?.motivo === 'datos_dictados_por_voz') {
      setAlChat({ mensaje: result.message || 'Los datos de cuenta se escriben, no se dictan.' });
    }
    // Registro por voz: faltan los numeros → mini-formulario tecleado (cedula + cuenta)
    if (result?.necesitaNumeros && result?.partialToken) {
      setNumForm({ partialToken: result.partialToken, datos: result.datos || {} });
      setNumDoc(''); setNumCta(''); setNumErr(null);
    }
    // Blockchain: tarjeta de deposito (direccion EN PANTALLA, jamas dictada)
    if (result?.depositCard && result?.direccion) {
      setCryptoDeposit({ red: result.red, activo: result.activo, direccion: result.direccion, advertencia: result.advertencia });
      setCopiado(false);
    }
    // Recarga por voz: boton PSE con el monto listo
    if (result?.rechargeReady && result.rechargeReady.url) {
      setRechargeCard(result.rechargeReady);
    }
    // Conversion COP<->USD cotizada (Business): tarjeta de confirmar con un toque
    if (result?.convertCard && result.convertCard.direccion) {
      setConvertCard(result.convertCard);
      setConvertMsg(null);
    }
    // Cobro creado por voz (Business): link EN PANTALLA (no se dicta la URL)
    if (result?.cobro?.link) {
      setCobroCard({
        link: result.cobro.link,
        cliente: result.cobro.clienteNombre || result.cobro.cliente || result.cobro?.cliente?.nombre,
        concepto: result.cobro.concepto,
        montoCop: result.cobro.montoCop,
        mensaje: result.mensaje,
      });
      setCobroCopiado(false);
    }
    // Blockchain: falta la direccion → formulario para PEGARLA
    if (result?.necesitaDireccion && result?.partialToken) {
      setCryptoForm({ partialToken: result.partialToken, montoUsd: result.montoUsd });
      setCryptoAddr(''); setCryptoErr(null);
    }
    // Metas: reequilibrio propuesto → tarjeta de UN TOQUE (sin SMS)
    if (result?.reallocToken) {
      setReallocTok(result.reallocToken); setReallocErr(null);
    }
    // Registro de cuenta EE.UU. por voz: faltan cuenta+routing+direccion → formulario tecleado
    if (result?.necesitaDatosIntl && result?.partialToken) {
      setIntlForm({ partialToken: result.partialToken, datos: result.datos || {} });
      setIntlF({ accountNumber: '', routingNumber: '', accountType: 'checking', street: '', city: '', state: '', postalCode: '' });
      setIntlErr(null);
    }
    // Capsula del dia → reproducir el audio real (voz oficial)
    if (name === 'get_capsula_hoy' && result?.audioUrl) {
      setCapsula({ title: result.title, audioUrl: result.audioUrl });
    }

    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'conversation.item.create',
        item: { type: 'function_call_output', call_id: callId, output: JSON.stringify(result).slice(0, 8000) },
      }));
      ws.send(JSON.stringify({ type: 'response.create' }));
    }
  }, []);

  // ---------- arranque de la sesion ----------
  useEffect(() => {
    let cancelled = false;

    async function start() {
      let cfg;
      try {
        const resp = await VOICE_API.post(`${basePath}/voice-session`, {});
        cfg = resp?.data?.data;
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(err?.response?.data?.message || 'No se pudo abrir la sesion de voz.');
        setStatus('error');
        return;
      }
      if (cancelled || !cfg?.token) return;

      // Microfono + contexto de audio a 24kHz
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
      } catch {
        setErrorMsg('Necesito permiso del microfono para conversar. Activalo y vuelve a intentar.');
        setStatus('error');
        return;
      }
      micStreamRef.current = stream;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx({ sampleRate: 24000 });
      audioCtxRef.current = ctx;
      playTimeRef.current = ctx.currentTime;

      // WebSocket directo a xAI con el token efimero en el protocolo
      const protoToken = cfg.token.startsWith('xai-client-secret.') ? cfg.token : `xai-client-secret.${cfg.token}`;
      const ws = new WebSocket(cfg.url, [protoToken]);
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        ws.send(JSON.stringify({
          type: 'session.update',
          session: {
            voice: cfg.voice,
            instructions: cfg.instructions,
            turn_detection: { type: 'server_vad' },
            audio: {
              input: { format: { type: 'audio/pcm', rate: 24000 } },
              output: { format: { type: 'audio/pcm', rate: 24000 } },
            },
            tools: cfg.tools || [],
          },
        }));
        // Disclaimer legal guionado (no pasa por el modelo)
        ws.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'force_message',
            role: 'assistant',
            interruptible: false,
            content: [{ type: 'output_text', text: 'Hola, soy el Oraculo de FlowexPay. Recuerda: no doy recomendaciones y ninguna operacion se ejecuta sin tu confirmacion en pantalla. ¿En que te ayudo?' }],
          },
        }));

        // Captura del microfono → PCM16 base64
        const srcNode = ctx.createMediaStreamSource(stream);
        const proc = ctx.createScriptProcessor(4096, 1, 1);
        micNodeRef.current = proc;
        proc.onaudioprocess = (e) => {
          if (ws.readyState !== WebSocket.OPEN) return;
          const pcm = floatTo16BitPCM(e.inputBuffer.getChannelData(0));
          ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: int16ToBase64(pcm) }));
        };
        srcNode.connect(proc);
        proc.connect(ctx.destination); // requerido por ScriptProcessor (no suena: buffer de salida vacio)

        setStatus('escuchando');

        // Tope duro de minutos por sesion
        const totalSecs = (cfg.maxMinutes || 10) * 60;
        setSecondsLeft(totalSecs);
        const startedAt = Date.now();
        sessionStartRef.current = startedAt; // para devolver minutos no usados al colgar
        timerRef.current = setInterval(() => {
          const left = totalSecs - Math.floor((Date.now() - startedAt) / 1000);
          setSecondsLeft(Math.max(0, left));
          if (left <= 0) closeSession();
        }, 1000);
      };

      ws.onmessage = (event) => {
        let msg;
        try { msg = JSON.parse(event.data); } catch { return; }
        switch (msg.type) {
          case 'response.output_audio.delta':
            if (msg.delta) playDelta(msg.delta);
            break;
          case 'input_audio_buffer.speech_started':
            flushPlayback(); // barge-in: el cliente interrumpe al Oraculo
            break;
          case 'response.function_call_arguments.done':
            runTool(msg.name, msg.call_id, msg.arguments);
            break;
          case 'response.output_audio_transcript.done':
          case 'response.output_text.done':
            pushTranscript('assistant', msg.transcript || msg.text);
            break;
          case 'conversation.item.input_audio_transcription.completed':
            pushTranscript('user', msg.transcript);
            break;
          case 'error':
            console.warn('[OraculoVoz] error del servidor:', msg);
            break;
          default:
            break;
        }
      };

      ws.onerror = () => {
        if (closedRef.current) return;
        setErrorMsg('Se cayo la conexion de voz. Intenta de nuevo.');
        setStatus('error');
      };
      ws.onclose = () => { if (!closedRef.current) closeSession(); };
    }

    start();
    const logInterval = setInterval(flushLog, 20000);
    return () => { cancelled = true; clearInterval(logInterval); closeSession(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- confirmacion SMS (mismo flujo del chat) ----------
  async function submitNumeros() {
    if (!numForm) return;
    const doc = numDoc.replace(/\D/g, ''); const cta = numCta.replace(/\D/g, '');
    if (doc.length < 5 || cta.length < 5) { setNumErr('Escribe la cedula y el numero de cuenta completos.'); return; }
    setNumBusy(true); setNumErr(null);
    try {
      const resp = await VOICE_API.post('/api/oraculo/complete-add-beneficiary', { partialToken: numForm.partialToken, numeroDocumento: doc, numeroCuenta: cta });
      const data = resp?.data?.data;
      if (!data || !data.tradeToken) { setNumErr((resp?.data?.message) || 'No se pudo continuar.'); setNumBusy(false); return; }
      setTradePreview({ tradeToken: data.tradeToken, preview: data.preview || [] });
      setSmsStep(null); setSmsCode(''); setSmsError(null);
      setNumForm(null); setNumBusy(false);
    } catch (err) {
      setNumErr(err?.response?.data?.message || 'No se pudo continuar el registro.');
      setNumBusy(false);
    }
  }

  // F-E por voz: el cliente tecleo cuenta + routing + direccion de la cuenta de EE.UU.
  async function submitIntl() {
    if (!intlForm) return;
    const acc = intlF.accountNumber.replace(/\D/g, ''); const rout = intlF.routingNumber.replace(/\D/g, '');
    if (acc.length < 4) { setIntlErr('Escribe el numero de cuenta.'); return; }
    if (rout.length !== 9) { setIntlErr('El routing debe tener exactamente 9 digitos.'); return; }
    if (!intlF.street || !intlF.city || !intlF.state || !intlF.postalCode) { setIntlErr('Completa la direccion: calle, ciudad, estado y ZIP.'); return; }
    setIntlBusy(true); setIntlErr(null);
    try {
      const resp = await VOICE_API.post('/api/oraculo/complete-add-intl-account', {
        partialToken: intlForm.partialToken,
        accountNumber: acc, routingNumber: rout, accountType: intlF.accountType, paymentRail: 'ach',
        address: { street: intlF.street, city: intlF.city, state: intlF.state, postalCode: intlF.postalCode, country: 'USA' },
      });
      const data = resp?.data?.data;
      if (!data || !data.tradeToken) { setIntlErr((resp?.data?.message) || 'No se pudo continuar.'); setIntlBusy(false); return; }
      setTradePreview({ tradeToken: data.tradeToken, preview: data.preview || [] });
      setSmsStep(null); setSmsCode(''); setSmsError(null);
      setIntlForm(null); setIntlBusy(false);
    } catch (err) {
      setIntlErr(err?.response?.data?.message || 'No se pudo continuar el registro.');
      setIntlBusy(false);
    }
  }

  // Reequilibrio de metas: un toque, sin SMS (no sale dinero de la cuenta).
  async function confirmRealloc() {
    if (reallocTok === null || reallocBusy) return;
    setReallocBusy(true); setReallocErr(null);
    const r = await oraculoExecuteReallocation(reallocTok);
    setReallocBusy(false);
    if (r.success && r.data) {
      setReallocTok(null);
      try {
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
          const texto = `¡Listo! ${String(r.data.mensaje || 'Reequilibrio aplicado entre tus metas.')} No salio dinero de tu cuenta. ¿Te ayudo con algo mas?`;
          ws.send(JSON.stringify({
            type: 'conversation.item.create',
            item: { type: 'force_message', role: 'assistant', interruptible: true, content: [{ type: 'output_text', text: texto }] },
          }));
        }
      } catch { /* la tarjeta ya desaparecio; la voz sigue */ }
    } else {
      setReallocErr(String(r.error || 'No se pudo reequilibrar. Intenta de nuevo.'));
    }
  }

  // Blockchain: el cliente PEGO la direccion → verificacion 3 capas en el server → tarjeta SMS
  async function submitCryptoAddr() {
    if (!cryptoForm || cryptoBusy) return;
    const dir = cryptoAddr.trim();
    if (dir.length < 30) { setCryptoErr('Pega la direccion completa (no la escribas a mano).'); return; }
    setCryptoBusy(true); setCryptoErr(null);
    try {
      const resp = await VOICE_API.post('/api/oraculo/complete-crypto-send', { partialToken: cryptoForm.partialToken, direccion: dir });
      const data = resp?.data?.data;
      if (!data || !data.tradeToken) { setCryptoErr((resp?.data?.message) || 'No se pudo continuar.'); setCryptoBusy(false); return; }
      setTradePreview({ tradeToken: data.tradeToken, preview: data.preview || [] });
      setSmsStep(null); setSmsCode(''); setSmsError(null);
      setCryptoForm(null); setCryptoBusy(false);
    } catch (err) {
      setCryptoErr(String(err?.response?.data?.message || 'No se pudo verificar la direccion.'));
      setCryptoBusy(false);
    }
  }

  async function handleConfirm() {
    setSmsStep('sending'); setSmsError(null); setEsDemo(false);
    const r = await oraculoSendCode(phone);
    if (!r.success) { setSmsError(r.error || 'No se pudo enviar el código.'); setSmsStep(null); return; }
    // Cuenta de pruebas: no hay SMS que esperar, se usa el codigo fijo.
    setEsDemo(!!r.demo);
    setSmsStep('code');
  }
  async function handleVerify() {
    if (smsCode.length !== 6) return;
    setSmsStep('executing'); setSmsError(null);
    const v = await oraculoVerifyTrade(tradePreview.tradeToken, smsCode, phone);
    if (!v.success) { setSmsError(v.error || 'Codigo incorrecto o expirado.'); setSmsStep('code'); return; }
    const ex = await oraculoExecuteTrade(tradePreview.tradeToken);
    // Solo es exito REAL si el backend confirmo Y hay filas de resultado. Un
    // resultado vacio o con error significa que NO se movio dinero: jamas
    // celebrar "enviado" sobre eso (bug real: envio a banco COL fallado que
    // se anunciaba como confirmado por voz). La voz calla y la tarjeta muestra
    // el error para reintentar.
    // Festejo falso NUNCA: las filas con Error no cuentan como ejecucion real
    // (incidente 21-jul: celebro "Compra de GUARDIAN: error").
    const filasTodas = (ex?.data?.results && Array.isArray(ex.data.results)) ? ex.data.results : [];
    const filasEjec = filasTodas.filter((r) => String(r.status || '').toLowerCase().indexOf('error') === -1);
    if (!ex.success || ex.data?.error || ex.data?.status === 'ERROR' || filasEjec.length === 0) {
      setSmsError(ex.data?.error || ex.error || 'La operacion no se completo. No se movio dinero. Intenta de nuevo.');
      setSmsStep('code');
      return;
    }
    setSmsStep('done');

    // La voz CELEBRA la ejecucion (hallazgo de la demo con Progresion: el
    // usuario tenia que contarle "ya hice la compra"). Texto guionado via
    // force_message — cifras exactas, sin dejar que el modelo las invente.
    // Solo llega aqui si filasEjec.length > 0 (ejecucion real).
    try {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        const filas = filasEjec.map((r) => {
          const estado = String(r.status || '');
          if (/Activa/i.test(estado)) return `${r.side} de ${r.notional} de ${r.symbol}: orden inteligente activa — ${estado.replace(/^Activa\s*—\s*/i, '')}`;
          return `${r.side} de ${r.notional} de ${r.symbol}: ${estado.toLowerCase() || 'enviada'}`;
        });
        const texto = `¡Listo! Operacion confirmada. ${filas.join('. ')}. ¿Te ayudo con algo mas?`;
        ws.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'force_message',
            role: 'assistant',
            interruptible: true,
            content: [{ type: 'output_text', text: texto }],
          },
        }));
      }
    } catch { /* la voz sigue; la tarjeta ya mostro el resultado */ }

    setTimeout(() => { setTradePreview(null); setSmsStep(null); setSmsCode(''); }, 4000);
  }

  const mins = secondsLeft != null ? `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}` : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(10,37,64,0.78)' }}>
      <div className="w-full max-w-md mx-4 rounded-2xl overflow-hidden shadow-2xl" style={{ background: `linear-gradient(160deg, ${NAVY} 0%, #123156 100%)` }}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4">
          <div className="flex items-center gap-2">
            <span className="text-lg">🔮</span>
            <span className="text-sm font-bold text-white">Oráculo — conversación de voz</span>
          </div>
          <div className="flex items-center gap-3">
            {mins && <span className="text-[11px] font-mono" style={{ color: GOLD }}>{mins}</span>}
            <button onClick={handleExit} className="text-white/70 hover:text-white text-xl leading-none" title="Colgar">✕</button>
          </div>
        </div>

        {/* LA PUERTA AL CHAT. Cuando la voz rechaza datos dictados (una cuenta,
            una cedula, una direccion) le dice al dueno que los escriba — y este
            modal tapaba el chat entero, asi que no habia donde. Ahora la salida
            existe: un boton que cuelga la voz y lo deja escribiendo. */}
        {alChat && (
          <div className="mx-5 mt-3 mb-1 rounded-xl p-4" style={{ background: 'rgba(201,169,97,0.10)', border: `1px solid ${GOLD}66` }}>
            <p className="text-sm font-bold mb-1" style={{ color: GOLD }}>Estos datos se escriben, no se dictan</p>
            <p className="text-[13px] text-white/80 leading-snug mb-3">{alChat.mensaje}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setAlChat(null); handleExit(); onIrAlChat?.(); }}
                className="flex-1 py-2.5 rounded-lg text-sm font-bold transition hover:opacity-90"
                style={{ background: GOLD, color: NAVY }}
              >
                Escribirlo en el chat
              </button>
              <button
                type="button"
                onClick={() => setAlChat(null)}
                className="px-4 py-2.5 rounded-lg text-sm font-semibold text-white/80 transition hover:text-white"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)' }}
              >
                Seguir hablando
              </button>
            </div>
          </div>
        )}

        {/* Orbe de estado */}
        <div className="flex flex-col items-center py-6">
          <div
            className="w-24 h-24 rounded-full flex items-center justify-center transition-all"
            style={{
              background: status === 'hablando' ? `radial-gradient(circle, ${GOLD} 0%, #8a6f35 100%)` : 'radial-gradient(circle, #2A59C6 0%, #16305e 100%)',
              boxShadow: status === 'hablando' ? `0 0 40px ${GOLD}88` : status === 'escuchando' ? '0 0 30px #2A59C688' : 'none',
              animation: (status === 'escuchando' || status === 'hablando') ? 'pulse 1.6s ease-in-out infinite' : 'none',
            }}
          >
            <span className="text-3xl">{status === 'hablando' ? '🔊' : status === 'error' ? '⚠️' : '🎙'}</span>
          </div>
          <div className="mt-3 text-sm font-medium text-white/90">
            {status === 'conectando' && 'Conectando con el Oraculo...'}
            {status === 'escuchando' && 'Te escucho — habla con confianza'}
            {status === 'hablando' && 'El Oraculo esta hablando'}
            {status === 'cerrado' && 'Sesion terminada'}
            {status === 'error' && (errorMsg || 'Error de conexion')}
          </div>
          {caption && status !== 'error' && (
            <div className="mt-2 px-6 text-center text-[12px] leading-snug text-white/60 max-h-16 overflow-hidden">“{caption}”</div>
          )}
        </div>

        {/* Capsula del dia */}
        {capsula && (
          <div className="mx-4 mb-3 rounded-xl px-4 py-3" style={{ background: 'rgba(255,255,255,0.08)', border: `1px solid ${GOLD}55` }}>
            <div className="text-[11px] font-semibold mb-1" style={{ color: GOLD }}>🎧 {capsula.title || 'Capsula del dia'}</div>
            <audio src={capsula.audioUrl} autoPlay controls className="w-full h-8" onEnded={() => setCapsula(null)} />
          </div>
        )}

        {/* Mini-formulario: cedula + numero de cuenta (registro por VOZ) */}
        {numForm && !tradePreview && (
          <div className="mx-4 mb-4 rounded-xl bg-white px-4 py-3">
            <div className="text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: NAVY }}>Datos de la cuenta</div>
            <div className="text-[12px] text-[#374151] mb-2">
              {numForm.datos?.beneficiario} · {numForm.datos?.banco} ({numForm.datos?.tipoCuenta})
            </div>
            <input value={numDoc} onChange={(e) => setNumDoc(e.target.value.replace(/\D/g, '').slice(0, 15))}
              placeholder={`Numero de documento (${numForm.datos?.tipoDoc || 'CC'})`} inputMode="numeric"
              className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none mb-2" />
            <input value={numCta} onChange={(e) => setNumCta(e.target.value.replace(/\D/g, '').slice(0, 20))}
              placeholder="Numero de cuenta" inputMode="numeric"
              className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none" />
            <div className="flex gap-2 mt-2.5">
              <button onClick={submitNumeros} disabled={numBusy}
                className="flex-1 py-2 text-[12px] font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: NAVY }}>
                {numBusy ? 'Un momento...' : 'Continuar'}
              </button>
              <button onClick={() => setNumForm(null)} className="px-3 py-2 text-[12px] text-[#6B7280] border border-[#E5E7EB] rounded-lg">Cancelar</button>
            </div>
            {numErr && <div className="text-[11px] text-red-500 mt-1.5">{String(numErr?.message || numErr)}</div>}
          </div>
        )}

        {/* Formulario de cuenta EE.UU. (registro por VOZ): cuenta + routing + direccion */}
        {intlForm && !tradePreview && (
          <div className="mx-4 mb-4 rounded-xl bg-white px-4 py-3">
            <div className="text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: NAVY }}>🇺🇸 Datos de la cuenta (EE.UU.)</div>
            <div className="text-[12px] text-[#374151] mb-2">
              {intlForm.datos?.ownerName} · {intlForm.datos?.bankName}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input value={intlF.accountNumber} onChange={(e) => setIntlF({ ...intlF, accountNumber: e.target.value.replace(/\D/g, '').slice(0, 20) })}
                placeholder="Numero de cuenta" inputMode="numeric"
                className="col-span-2 w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none" />
              <input value={intlF.routingNumber} onChange={(e) => setIntlF({ ...intlF, routingNumber: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                placeholder="Routing (9 digitos)" inputMode="numeric"
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${intlF.routingNumber && intlF.routingNumber.length !== 9 ? 'border-red-400 bg-red-50' : 'border-[#E5E7EB]'}`} />
              <select value={intlF.accountType} onChange={(e) => setIntlF({ ...intlF, accountType: e.target.value })}
                className="w-full px-2 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none bg-white">
                <option value="checking">Checking</option><option value="savings">Savings</option>
              </select>
              <input value={intlF.street} onChange={(e) => setIntlF({ ...intlF, street: e.target.value })}
                placeholder="Direccion (calle)" className="col-span-2 w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none" />
              <input value={intlF.city} onChange={(e) => setIntlF({ ...intlF, city: e.target.value })}
                placeholder="Ciudad" className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none" />
              <input value={intlF.state} onChange={(e) => setIntlF({ ...intlF, state: e.target.value })}
                placeholder="Estado (ej: FL)" className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none" />
              <input value={intlF.postalCode} onChange={(e) => setIntlF({ ...intlF, postalCode: e.target.value })}
                placeholder="ZIP" inputMode="numeric" className="w-full px-3 py-2 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none" />
            </div>
            <div className="flex gap-2 mt-2.5">
              <button onClick={submitIntl} disabled={intlBusy}
                className="flex-1 py-2 text-[12px] font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: NAVY }}>
                {intlBusy ? 'Un momento...' : 'Continuar → SMS'}
              </button>
              <button onClick={() => setIntlForm(null)} className="px-3 py-2 text-[12px] text-[#6B7280] border border-[#E5E7EB] rounded-lg">Cancelar</button>
            </div>
            {intlErr && <div className="text-[11px] text-red-500 mt-1.5">{String(intlErr?.message || intlErr)}</div>}
          </div>
        )}

        {/* Blockchain: tarjeta de DEPOSITO — direccion en pantalla con boton copiar */}
        {cryptoDeposit && !tradePreview && (
          <div className="mx-4 mb-4 rounded-xl bg-white px-4 py-3">
            <div className="text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: NAVY }}>Recibir por blockchain</div>
            <div className="flex gap-2 mb-2">
              <span className="text-[11px] font-bold px-2 py-1 rounded-lg" style={{ background: '#E8F5E9', color: '#1B5E20' }}>Red: {cryptoDeposit.red}</span>
              <span className="text-[11px] font-bold px-2 py-1 rounded-lg" style={{ background: '#E3F2FD', color: '#0D47A1' }}>Activo: {cryptoDeposit.activo}</span>
            </div>
            <div className="text-[11px] font-mono break-all bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg px-2.5 py-2 text-[#111827]">{cryptoDeposit.direccion}</div>
            <button
              onClick={() => { try { navigator.clipboard.writeText(cryptoDeposit.direccion); setCopiado(true); setTimeout(() => setCopiado(false), 2500); } catch { /* clipboard bloqueado */ } }}
              className="w-full mt-2 py-2 text-[12px] font-semibold text-white rounded-lg" style={{ backgroundColor: copiado ? '#16A34A' : NAVY }}>
              {copiado ? 'Copiada ✅' : 'Copiar direccion'}
            </button>
            <div className="text-[10px] text-[#B45309] mt-1.5">{cryptoDeposit.advertencia}</div>
            <button onClick={() => setCryptoDeposit(null)} className="w-full mt-1 text-[11px] text-[#9CA3AF] hover:underline">Cerrar</button>
          </div>
        )}

        {/* Blockchain: formulario para PEGAR la direccion destino (nunca dictada) */}
        {cryptoForm && !tradePreview && (
          <div className="mx-4 mb-4 rounded-xl bg-white px-4 py-3">
            <div className="text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: NAVY }}>Enviar por blockchain — red Solana</div>
            <div className="text-[12px] text-[#374151] mb-2">Monto: <b>{'$' + Number(cryptoForm.montoUsd).toFixed(2)} USD</b>. Pega la dirección Solana tal cual te la compartieron (no la escribas a mano).</div>
            <input value={cryptoAddr} onChange={(e) => setCryptoAddr(e.target.value)} placeholder="Pega aqui la dirección Solana"
              className="w-full px-3 py-2 text-[12px] font-mono border border-[#E5E7EB] rounded-lg focus:outline-none" />
            <div className="flex gap-2 mt-2.5">
              <button onClick={submitCryptoAddr} disabled={cryptoBusy}
                className="flex-1 py-2 text-[12px] font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: NAVY }}>
                {cryptoBusy ? 'Verificando en la red Solana...' : 'Verificar direccion →'}
              </button>
              <button onClick={() => setCryptoForm(null)} className="px-3 py-2 text-[12px] text-[#6B7280] border border-[#E5E7EB] rounded-lg">Cancelar</button>
            </div>
            {cryptoErr && <div className="text-[11px] text-red-500 mt-1.5">{cryptoErr}</div>}
          </div>
        )}

        {/* Conversion COP<->USD cotizada por voz (Business): confirmar con un toque.
            Llama el MISMO endpoint protegido del boton Convertir; la voz solo cotizo. */}
                {cobroCard && !tradePreview && (
          <div className="mx-4 mb-3 rounded-xl border border-[#C7D7FE] bg-[#F8FAFE] p-3 text-[#181D27]">
            <div className="text-[11px] font-extrabold uppercase tracking-wide text-[#2A59C6] mb-1">Cobro listo</div>
            <div className="text-[13px] font-semibold">
              {cobroCard.cliente || 'Cliente'} · {cobroCard.montoCop != null ? `$${Number(cobroCard.montoCop).toLocaleString('es-CO')}` : ''}
            </div>
            {cobroCard.concepto && <div className="text-[12px] text-[#535862] mt-0.5">{cobroCard.concepto}</div>}
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(cobroCard.link); setCobroCopiado(true); setTimeout(() => setCobroCopiado(false), 2000); } catch { /* ignore */ }
                }}
                className="flex-1 py-2 rounded-lg bg-[#092F7B] text-white text-[12px] font-semibold"
              >
                {cobroCopiado ? '¡Link copiado!' : 'Copiar link de cobro'}
              </button>
              <button type="button" onClick={() => setCobroCard(null)} className="px-3 py-2 text-[12px] text-[#6B7280] border border-[#E5E7EB] rounded-lg">Cerrar</button>
            </div>
            <p className="text-[10px] text-[#9CA3AF] mt-2 break-all">{cobroCard.link}</p>
          </div>
        )}

{convertCard && !tradePreview && (
          <div className="mx-4 mb-4 rounded-xl bg-white px-4 py-3" style={{ border: '1.5px solid #C9A961' }}>
            <div className="text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: NAVY }}>Conversión lista</div>
            <div className="text-[12px] text-[#374151] mb-2">
              Se debitan <b>{convertCard.debita}</b> (comisión {convertCard.comision} incluida) y recibes <b>{convertCard.recibeAprox}</b> aprox. · {convertCard.tasa}
            </div>
            {convertMsg && (
              <div className={`text-[11px] mb-2 ${convertMsg.ok ? 'text-green-600' : 'text-red-500'}`}>{convertMsg.text}</div>
            )}
            {!convertMsg?.ok && (
              <div className="flex gap-2">
                <button
                  disabled={convertBusy}
                  onClick={async () => {
                    setConvertBusy(true);
                    try {
                      const res = await API.post('/transaction/convert', { direction: convertCard.direccion, amount: convertCard.monto });
                      if (res.data?.success) {
                        setConvertMsg({ ok: true, text: `Convertido. Nuevo saldo: $${Number(res.data.data?.balance_cop || 0).toLocaleString('es-CO')} COP · $${Number(res.data.data?.balance_usdc || 0).toFixed(2)} USD` });
                      } else {
                        setConvertMsg({ ok: false, text: res.data?.message || 'No se pudo convertir' });
                      }
                    } catch (e) {
                      setConvertMsg({ ok: false, text: e?.response?.data?.message || 'No se pudo convertir. Intenta de nuevo.' });
                    } finally { setConvertBusy(false); }
                  }}
                  className="flex-1 py-2 text-[12px] font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: NAVY }}>
                  {convertBusy ? 'Convirtiendo…' : 'Convertir ahora'}
                </button>
                <button onClick={() => setConvertCard(null)} className="px-3 py-2 text-[12px] text-[#6B7280] border border-[#E5E7EB] rounded-lg">Ahora no</button>
              </div>
            )}
            {convertMsg?.ok && (
              <button onClick={() => { setConvertCard(null); setConvertMsg(null); }} className="w-full py-2 text-[12px] font-semibold text-[#374151] border border-[#E5E7EB] rounded-lg">Listo</button>
            )}
          </div>
        )}

        {/* Recarga por voz: boton PSE con el monto listo (autoriza el banco) */}
        {rechargeCard && !tradePreview && (
          <div className="mx-4 mb-4 rounded-xl bg-white px-4 py-3">
            <div className="text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: NAVY }}>Recarga lista — PSE</div>
            <div className="text-[12px] text-[#374151] mb-2">
              Monto puesto: <b>{'$' + Number(rechargeCard.montoCop).toLocaleString('es-CO') + ' COP'}</b>. Al continuar eliges tu banco y autorizas en su portal — la plata llega al aprobar.
            </div>
            <div className="flex gap-2">
              <button onClick={() => { window.location.href = rechargeCard.url; }}
                className="flex-1 py-2 text-[12px] font-semibold text-white rounded-lg" style={{ backgroundColor: NAVY }}>
                Continuar a PSE →
              </button>
              <button onClick={() => setRechargeCard(null)} className="px-3 py-2 text-[12px] text-[#6B7280] border border-[#E5E7EB] rounded-lg">Ahora no</button>
            </div>
          </div>
        )}

        {/* Reequilibrio de metas: UN TOQUE, sin SMS (no sale dinero de la cuenta) */}
        {reallocTok && (
          <div className="mx-4 mb-4 rounded-xl bg-white px-4 py-3">
            <div className="text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: NAVY }}>Reequilibrio de metas propuesto</div>
            <div className="text-[12px] text-[#374151] mb-2">No sale dinero de tu cuenta — solo reorganizo el valor entre tus metas. Es reversible.</div>
            <div className="flex gap-2">
              <button onClick={confirmRealloc} disabled={reallocBusy}
                className="flex-1 py-2 text-[12px] font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: NAVY }}>
                {reallocBusy ? 'Aplicando...' : 'Confirmar reequilibrio'}
              </button>
              <button onClick={() => setReallocTok(null)} className="px-3 py-2 text-[12px] text-[#6B7280] border border-[#E5E7EB] rounded-lg">Ahora no</button>
            </div>
            {reallocErr && <div className="text-[11px] text-red-500 mt-1.5">{reallocErr}</div>}
          </div>
        )}

        {/* Tarjeta de operacion preparada + SMS.
            Dos formas de preview: trades de Alpaca (symbol/side/notional) y operaciones
            con `operacion` + campos etiquetados (registros, envios, conversiones) — cada
            una se pinta a su manera; el "Total con comisiones" solo aplica a trades. */}
        {opciones && (
        <div className="bg-white rounded-xl p-4 mt-3 text-left">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="text-[13px] font-bold text-[#181D27]">
              Opciones {opciones.tipo === 'put' ? 'put' : 'call'} de {opciones.symbol}
            </div>
            <button onClick={() => setOpciones(null)} className="text-[11px] text-[#9CA3AF]">Cerrar</button>
          </div>

          <div className="text-[12px] text-[#6B7280] leading-snug mb-3">
            {opciones.contratos.length} contratos para el {opciones.vencimientoMostrado}
            {opciones.vencimientos.length > 1 && ` y ${opciones.vencimientos.length - 1} vencimiento(s) más`}.
            Cada contrato son 100 acciones.
          </div>

          {/* LO QUE PIDIO IVAN: que lo LLEVE a la pestaña de opciones de la
              accion, donde estan TODAS con su cadena completa — no una tablita
              recortada dentro del modal de voz. */}
          <button
            onClick={() => {
              window.dispatchEvent(new CustomEvent('flowex:abrir-accion', {
                detail: { symbol: opciones.symbol, tab: 'opciones' },
              }));
              setOpciones(null);
              onClose?.();
            }}
            className="w-full py-3 bg-[#2A59C6] text-white rounded-xl text-[13px] font-semibold hover:bg-[#1E4BB8] transition"
          >
            Ver todas las opciones de {opciones.symbol}
          </button>

          {opciones.advertencia && (
            <p className="mt-2 text-[11px] text-[#B54708] bg-[#FFFAEB] border border-[#F4D68C] rounded-lg px-3 py-2 leading-snug">
              {opciones.advertencia}
            </p>
          )}
        </div>
      )}

      {tradePreview && (
          <div className="mx-4 mb-4 rounded-xl bg-white px-4 py-3">
            <div className="text-[11px] font-bold uppercase tracking-wide mb-1.5" style={{ color: NAVY }}>
              {(tradePreview.preview || [])[0]?.operacion ? 'Preparado por voz — revisa y confirma' : 'Operacion preparada por voz'}
            </div>
            {(tradePreview.preview || []).map((p, i) => (
              p.operacion ? (
                <div key={i} className="text-[12px] text-[#374151] py-0.5">
                  <div className="font-semibold text-[#111827] mb-1">{p.operacion}</div>
                  {Object.entries(p).filter(([k, v]) => !['operacion', 'nota', 'vence'].includes(k) && v).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3 py-0.5">
                      <span className="text-[#6B7280] capitalize shrink-0">{k.replace(/_/g, ' ')}</span>
                      <span className="font-medium text-right break-words">{String(v)}</span>
                    </div>
                  ))}
                  {p.nota && <div className="text-[10px] text-[#6B7280] mt-1">{p.nota}</div>}
                  {p.vence && <div className="text-[10px] text-[#B45309] mt-0.5">{p.vence}</div>}
                </div>
              ) : (
                <div key={i} className="flex justify-between text-[12px] text-[#374151] py-0.5">
                  <span>{p.side === 'sell' ? 'Venta' : 'Compra'} {p.symbol}{p.triggerPrice ? ` si ${p.triggerCondition === 'above' ? 'sube a' : 'baja a'} $${p.triggerPrice}` : ''}</span>
                  <span className="font-semibold">
                    {p.notional
                      // Llegaba "$175.00" y se le anteponia otro $: "$$175.00".
                      // Se limpia a numero y se formatea una sola vez.
                      ? `$${Number(String(p.notional).replace(/[^0-9.]/g, '') || 0).toFixed(2)}`
                      : ''}
                  </span>
                </div>
              )
            ))}
            {(tradePreview.totalDebit || tradePreview.totalNotional) && (
              <div className="flex justify-between text-[12px] border-t border-[#E5E7EB] mt-1.5 pt-1.5 text-[#111827]">
                <span>Total con comisiones</span><span className="font-bold">{tradePreview.totalDebit || tradePreview.totalNotional}</span>
              </div>
            )}
            <div className="text-[10px] text-[#6B7280] mt-1">Esta operación es tu decisión. El Oráculo no da recomendaciones.</div>

            {smsStep == null && (
              <div className="flex gap-2 mt-2.5">
                <button onClick={handleConfirm} className="flex-1 py-2 text-[12px] font-semibold text-white rounded-lg" style={{ backgroundColor: NAVY }}>Confirmar con SMS</button>
                <button onClick={() => setTradePreview(null)} className="px-3 py-2 text-[12px] text-[#6B7280] border border-[#E5E7EB] rounded-lg">Cancelar</button>
              </div>
            )}
            {smsStep === 'sending' && <div className="text-[12px] text-[#6B7280] mt-2">Enviando código SMS…</div>}
            {smsStep === 'code' && esDemo && (
              <div className="text-[12px] text-[#B54708] bg-[#FFFAEB] border border-[#F4D68C] rounded-lg px-3 py-2 mt-2 leading-snug">
                Cuenta de pruebas: no se envía SMS. Usa tu código fijo de demostración.
              </div>
            )}
            {(smsStep === 'code' || smsStep === 'executing') && (
              <div className="mt-2.5">
                <input
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Código de 6 dígitos"
                  inputMode="numeric"
                  className="w-full px-3 py-2 text-center text-sm tracking-[0.4em] border border-[#E5E7EB] rounded-lg focus:outline-none"
                  disabled={smsStep === 'executing'}
                />
                <button onClick={handleVerify} disabled={smsCode.length !== 6 || smsStep === 'executing'}
                  className="w-full mt-2 py-2 text-[12px] font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: NAVY }}>
                  {smsStep === 'executing'
                    ? (/registrar/i.test((tradePreview.preview || [])[0]?.operacion || '') ? 'Registrando...' : 'Ejecutando...')
                    : (/registrar/i.test((tradePreview.preview || [])[0]?.operacion || '') ? 'Verificar y registrar' : 'Verificar y ejecutar')}
                </button>
              </div>
            )}
            {smsStep === 'done' && (
              <div className="text-[12px] font-semibold text-green-600 mt-2">
                {/registrar/i.test((tradePreview.preview || [])[0]?.operacion || '')
                  ? '✅ Cuenta registrada. Ya aparece en tus destinos.'
                  : '✅ Operacion enviada. El Oraculo te confirma el estado.'}
              </div>
            )}
            {smsError && <div className="text-[11px] text-red-500 mt-1.5">{String(smsError?.message || smsError)}</div>}
          </div>
        )}

        {/* PASAR AL CHAT — SIEMPRE VISIBLE, no solo cuando algo se rechaza.
            La tarjeta de arriba solo aparece si la herramienta llego a llamarse
            y fue rechazada; pero cuando el Oraculo hace caso a su instruccion y
            avisa ANTES ("escribemelo en el chat"), no hay ninguna herramienta de
            por medio — y era justo el caso de la primera prueba. Un dueno al que
            le dicen "escribelo" tiene que poder hacerlo en ese momento, sin
            adivinar que hay que colgar primero. */}
        {onIrAlChat && (
          <div className="px-5 pb-1">
            <button
              type="button"
              onClick={() => { handleExit(); onIrAlChat(); }}
              className="w-full py-2 rounded-lg text-[12px] font-semibold text-white/75 transition hover:text-white"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)' }}
            >
              ¿Tienes que escribir datos? Pasar al chat
            </button>
          </div>
        )}

        {/* Pie */}
        <div className="px-5 pb-4 text-center text-[10px] text-white/45">
          Conversación registrada · El Oráculo no da recomendaciones · Ninguna operación se ejecuta sin tu confirmación
        </div>
      </div>
    </div>
  );
}
